import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Mail, Users, Upload, Send, Search, Plus, Trash2, Eye,
  BarChart3, Clock, CheckCircle, AlertCircle, FileText,
  Download, RefreshCw, Filter, ChevronDown, Calendar,
  Zap, Bell, TrendingUp, Play
} from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminEmailMarketing() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('contacts');
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importTags, setImportTags] = useState('');
  const [campaignData, setCampaignData] = useState({
    name: '',
    subject: '',
    preview_text: '',
    html_content: '',
    target_all: true
  });
  const [testEmailData, setTestEmailData] = useState({
    to_email: '',
    template_type: 'welcome'
  });
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, [navigate, token, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load stats
      const statsRes = await axios.get(`${API_URL}/api/email-marketing/contacts/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);
      
      // Load based on active tab
      if (activeTab === 'contacts') {
        const contactsRes = await axios.get(`${API_URL}/api/email-marketing/contacts?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContacts(contactsRes.data.contacts || []);
      } else if (activeTab === 'campaigns') {
        const campaignsRes = await axios.get(`${API_URL}/api/email-marketing/campaigns`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCampaigns(campaignsRes.data.campaigns || []);
      } else if (activeTab === 'templates') {
        const templatesRes = await axios.get(`${API_URL}/api/email-marketing/templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTemplates(templatesRes.data.templates || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      setError('Selecciona un archivo CSV');
      return;
    }

    try {
      setError('');
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('source', 'csv_import');
      formData.append('tags', importTags);

      const response = await axios.post(`${API_URL}/api/email-marketing/contacts/import-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccessMsg(`Importados: ${response.data.imported}, Omitidos: ${response.data.skipped}`);
      setShowImportModal(false);
      setImportFile(null);
      setImportTags('');
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al importar');
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignData.name || !campaignData.subject || !campaignData.html_content) {
      setError('Completa todos los campos requeridos');
      return;
    }

    try {
      setError('');
      await axios.post(`${API_URL}/api/email-marketing/campaigns`, campaignData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccessMsg('Campaña creada exitosamente');
      setShowCampaignModal(false);
      setCampaignData({ name: '', subject: '', preview_text: '', html_content: '', target_all: true });
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear campaña');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('¿Enviar esta campaña a todos los contactos suscritos?')) return;

    try {
      const response = await axios.post(
        `${API_URL}/api/email-marketing/campaigns/${campaignId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMsg(response.data.message);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al enviar campaña');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailData.to_email) {
      setError('Ingresa un email');
      return;
    }

    try {
      setError('');
      await axios.post(`${API_URL}/api/email-marketing/test-email`, testEmailData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMsg(`Email de prueba enviado a ${testEmailData.to_email}`);
      setShowTestEmailModal(false);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al enviar email de prueba');
    }
  };

  const handleTriggerReminders = async () => {
    try {
      await axios.post(
        `${API_URL}/api/email-marketing/send-reminders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMsg('Procesando recordatorios de eventos...');
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al procesar recordatorios');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('¿Eliminar este contacto?')) return;

    try {
      await axios.delete(`${API_URL}/api/email-marketing/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filteredContacts = contacts.filter(c =>
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-email-marketing">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="w-7 h-7 text-blue-500" />
              Email Marketing
            </h1>
            <p className="text-gray-500 text-sm">Contactos, campañas y funnels automatizados</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleTriggerReminders}
              className="flex items-center gap-2 px-4 py-2 border border-orange-200 text-orange-600 rounded-lg hover:bg-orange-50"
              title="Enviar recordatorios de eventos próximos"
            >
              <Bell className="w-4 h-4" />
              Enviar Recordatorios
            </button>
            <button
              onClick={() => setShowTestEmailModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <Play className="w-4 h-4" />
              Probar Email
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={() => setError('')} className="ml-auto">✕</button>
          </div>
        )}
        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {successMsg}
            <button onClick={() => setSuccessMsg('')} className="ml-auto">✕</button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_contacts || 0}</p>
                <p className="text-sm text-gray-500">Total Contactos</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.subscribed || 0}</p>
                <p className="text-sm text-gray-500">Suscritos</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.active_rate || 0}%</p>
                <p className="text-sm text-gray-500">Tasa Activa</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Send className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.emails_sent_this_month || 0}</p>
                <p className="text-sm text-gray-500">Emails Este Mes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-100 mb-6">
          <div className="flex border-b border-gray-100">
            {[
              { key: 'contacts', label: 'Contactos', icon: Users },
              { key: 'campaigns', label: 'Campañas', icon: Mail },
              { key: 'templates', label: 'Templates', icon: FileText },
              { key: 'automation', label: 'Automatización', icon: Zap }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="p-6">
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex-1 min-w-64">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Buscar contactos..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Upload className="w-4 h-4" />
                  Importar CSV
                </button>
              </div>

              {loading ? (
                <div className="text-center py-12 text-gray-500">Cargando contactos...</div>
              ) : filteredContacts.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">No hay contactos</p>
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Importar contactos
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Nombre</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Estado</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Emails</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredContacts.map(contact => (
                        <tr key={contact.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900">{contact.email}</td>
                          <td className="px-4 py-3 text-gray-600">{contact.name || '-'}</td>
                          <td className="px-4 py-3">
                            {contact.is_subscribed ? (
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Suscrito</span>
                            ) : (
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">No suscrito</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {contact.total_emails_sent || 0} enviados
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleDeleteContact(contact.id)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && (
            <div className="p-6">
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => setShowCampaignModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Plus className="w-4 h-4" />
                  Nueva Campaña
                </button>
              </div>

              {loading ? (
                <div className="text-center py-12 text-gray-500">Cargando campañas...</div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">No hay campañas</p>
                  <button
                    onClick={() => setShowCampaignModal(true)}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Crear primera campaña
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {campaigns.map(campaign => (
                    <div key={campaign.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900">{campaign.name}</h3>
                          <p className="text-sm text-gray-500">{campaign.subject}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {campaign.total_sent || 0} enviados
                            </p>
                            <p className="text-xs text-gray-500">
                              {campaign.open_rate}% abiertos
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            campaign.status === 'sent' ? 'bg-green-100 text-green-700' :
                            campaign.status === 'sending' ? 'bg-yellow-100 text-yellow-700' :
                            campaign.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {campaign.status}
                          </span>
                          {campaign.status === 'draft' && (
                            <button
                              onClick={() => handleSendCampaign(campaign.id)}
                              className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600"
                            >
                              Enviar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map(template => (
                  <div key={template.key} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900 capitalize">
                          {template.key.replace(/_/g, ' ')}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                        <p className="text-xs text-gray-400 mt-2 font-mono">{template.subject}</p>
                      </div>
                      <button
                        onClick={() => {
                          setTestEmailData({ ...testEmailData, template_type: template.key });
                          setShowTestEmailModal(true);
                        }}
                        className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50"
                      >
                        Probar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Automation Tab */}
          {activeTab === 'automation' && (
            <div className="p-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Recordatorios Automáticos
                </h3>
                <p className="text-blue-700 mb-4">
                  El sistema envía automáticamente recordatorios a los usuarios registrados en eventos:
                </p>
                <ul className="space-y-2 text-blue-800">
                  <li className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <strong>24 horas antes</strong> - "Mañana es el día"
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <strong>1 hora antes</strong> - "Comienza en 1 hora"
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <strong>15 minutos antes</strong> - "¡Estamos por comenzar!"
                  </li>
                </ul>
                <button
                  onClick={handleTriggerReminders}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Ejecutar Recordatorios Ahora
                </button>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
                <h3 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Funnels de Conversión
                </h3>
                <p className="text-green-700 mb-4">
                  Secuencias automáticas para convertir usuarios:
                </p>
                <ul className="space-y-2 text-green-800">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <strong>Día 3</strong> - "¿Qué te pareció el evento?"
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <strong>Día 7</strong> - "Contenido relacionado recomendado"
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <strong>Día 14</strong> - "Oferta especial de membresía"
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Import CSV Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">Importar Contactos</h3>
                  <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Archivo CSV
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Columnas: email, name (opcional), phone (opcional)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (separados por coma)
                  </label>
                  <input
                    type="text"
                    value={importTags}
                    onChange={(e) => setImportTags(e.target.value)}
                    placeholder="leads, webinar, febrero"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleImportCSV}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Importar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Campaign Modal */}
        {showCampaignModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">Nueva Campaña</h3>
                  <button onClick={() => setShowCampaignModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la campaña *</label>
                  <input
                    type="text"
                    value={campaignData.name}
                    onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    placeholder="Campaña Febrero 2025"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Asunto del email *</label>
                  <input
                    type="text"
                    value={campaignData.subject}
                    onChange={(e) => setCampaignData({ ...campaignData, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    placeholder="🎓 Nuevo contenido disponible en UPIM"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contenido HTML *</label>
                  <textarea
                    value={campaignData.html_content}
                    onChange={(e) => setCampaignData({ ...campaignData, html_content: e.target.value })}
                    rows={10}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-mono text-sm"
                    placeholder="<html>..."
                  />
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={campaignData.target_all}
                    onChange={(e) => setCampaignData({ ...campaignData, target_all: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Enviar a todos los contactos suscritos</span>
                </label>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowCampaignModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreateCampaign}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Crear Campaña
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Test Email Modal */}
        {showTestEmailModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">Enviar Email de Prueba</h3>
                  <button onClick={() => setShowTestEmailModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email destino *</label>
                  <input
                    type="email"
                    value={testEmailData.to_email}
                    onChange={(e) => setTestEmailData({ ...testEmailData, to_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    placeholder="tu@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                  <select
                    value={testEmailData.template_type}
                    onChange={(e) => setTestEmailData({ ...testEmailData, template_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  >
                    <option value="welcome">Bienvenida</option>
                    <option value="reminder_24h">Recordatorio 24h</option>
                    <option value="reminder_1h">Recordatorio 1h</option>
                    <option value="reminder_15min">Recordatorio 15min</option>
                    <option value="post_event">Post-evento</option>
                    <option value="conversion_day3">Conversión día 3</option>
                    <option value="new_content">Nuevo contenido</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowTestEmailModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSendTestEmail}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Enviar Prueba
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
